import {Component, Input, OnChanges, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {Book} from "../../../../../model/Book";
import {BookService} from "../../../../service-data/book.service";
import {BodyService} from "../../../../service-data/body.service";
import {ResourceService} from "../../../../service-data/resource.service";
import {bestAvailableStage, PdfArtifactStatus} from "../../../../service-ui/pdf-artifact-stage";
import {requestStagedImage, StageWatch} from "../../../../service-ui/staged-preview-image";

/**
 * Read-only spread viewer for finalized Book interior pages.
 *
 * Uses `BookService.getImageURL(..., 'BODY', ...)`, which is the endpoint that
 * serves the **standardized** render — the pages that will actually be sent to
 * print. No shift/scale controls: a Book is immutable, so there is nothing to
 * adjust.
 *
 * ── Why this component listens (added 2026-09-15) ────────────────────────
 * A Book interior passes through two server-side stages before that endpoint
 * has anything final to give: PREPARE (`Resource.prepared`) and STANDARDIZE
 * (`Body.standardized`). Until both are COMPLETE the image on screen is a
 * stand-in — see `docs/architecture/pdf-pipeline.md` §3.
 *
 * Previously nothing here noticed either of them finishing:
 *
 *   - `ngOnChanges` was the only trigger, and it cannot fire for this.
 *     `AbstractLiveDataService.addUpdateLocalItem` mutates the cached `Book` /
 *     `Body` / `Resource` **in place**, so the `[book]` @Input never changes
 *     identity and Angular never re-runs the preview code.
 *   - The image cache was self-poisoning: a src-less `<img>` was appended
 *     under the real id before the URL arrived, so every later attempt read it
 *     as a hit and never requested again.
 *
 * Both are fixed. The component now subscribes to `bodyService.updateNotifier`
 * (for `standardized`) and `resourceService.updateNotifier` (for `prepared`),
 * and `requestStagedImage` carries the shared, correct request path.
 */
@Component({
  selector: 'app-book-preview-spread',
  templateUrl: './book-preview-spread.component.html',
  styleUrls: ['./book-preview-spread.component.css']
})
export class BookPreviewSpreadComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('leftImage', { static: true }) leftImage;
  @ViewChild('rightImage', { static: true }) rightImage;

  @Input() book: Book;
  @Input() leftPageNumber: number;
  @Input() rightPageNumber: number;
  @Input() dpi: number = 288;

  private readonly DISPLAY_DPI = 288;
  private readonly POINTS_PER_INCH = 72;

  private onDestroySubject: Subject<boolean> = new Subject<boolean>();
  private stageWatch = new StageWatch();

  /** Page display width in pixels at 288 DPI based on trim size */
  get pageDisplayWidth(): number {
    if (this.book && this.book.body && this.book.body.width) {
      return Math.round((this.book.body.width / this.POINTS_PER_INCH) * this.DISPLAY_DPI);
    }
    return 1152;
  }

  /** Page display height in pixels at 288 DPI based on trim size */
  get pageDisplayHeight(): number {
    if (this.book && this.book.body && this.book.body.height) {
      return Math.round((this.book.body.height / this.POINTS_PER_INCH) * this.DISPLAY_DPI);
    }
    return 1728;
  }

  constructor(private bookService: BookService,
              private bodyService: BodyService,
              private resourceService: ResourceService) {}

  ngOnInit() {
    this.refreshImages();

    // `prepared` lives on the Resource...
    this.resourceService.updateNotifier
      .pipe(takeUntil(this.onDestroySubject))
      .subscribe(resource => {
        if (resource && resource.getId && this.book && this.book.body
          && resource.getId() === this.book.body.resource_ID) {
          this.onStageMayHaveChanged();
        }
      });

    // ...and `standardized` lives on the Body. Two rows, two notifications,
    // and either one can be the thing that makes a better image available.
    this.bodyService.updateNotifier
      .pipe(takeUntil(this.onDestroySubject))
      .subscribe(body => {
        if (body && body.getId && this.book && this.book.body
          && body.getId() === this.book.body.ID) {
          this.onStageMayHaveChanged();
        }
      });
  }

  ngOnChanges() {
    this.refreshImages();
  }

  ngOnDestroy() {
    this.onDestroySubject.next(true);
    this.onDestroySubject.complete();
  }

  /**
   * Which artefact stage these pages should be showing.
   *
   * `standardizes: true` is not decoration. This is a Book, so its final stage
   * is STANDARDIZED even in the window before the `Body` row has loaded —
   * without the override, a missing row would read as "no standardize stage",
   * i.e. as a Draft, and a PREPARED render would be briefly declared final.
   */
  private artifactStatus(): PdfArtifactStatus {
    const body = this.book ? this.book.body : null;
    const resourceId = (body && body.resource_ID) || 0;
    const resource = resourceId ? this.resourceService.getLocalItem(resourceId) : null;
    return {
      resourceId: resourceId,
      prepared: resource ? resource.prepared : undefined,
      standardized: body ? body.standardized : undefined,
      standardizes: true
    };
  }

  /**
   * Re-render only when the stage genuinely moved.
   *
   * Both notifiers fire for every row of their type — notifications are
   * broadcast to every session on the role and are not filtered per user
   * (`shared/Notifications.md` §5). The id check above narrows it to this
   * Book; `StageWatch` narrows it to changes that alter the image.
   */
  private onStageMayHaveChanged(): void {
    if (!this.stageWatch.changed(this.artifactStatus())) {
      return;
    }
    this.refreshImages();
  }

  private refreshImages() {
    this.refreshPage(this.leftPageNumber, this.leftImage, true);
    this.refreshPage(this.rightPageNumber, this.rightImage, false);
  }

  private refreshPage(pageNumber: number, imageRef: any, isEvenPage: boolean) {
    if (!this.book || !this.book.body || !this.book.ID) {
      return;
    }

    const numPages = this.book.body.numPages;
    if (pageNumber === 0 || pageNumber > numPages) {
      if (imageRef && imageRef.nativeElement) {
        imageRef.nativeElement.src = '';
        imageRef.nativeElement.style.display = 'none';
        imageRef.nativeElement.parentElement.style.background = 'url("assets/images/noPageTile.png") repeat';
      }
      return;
    }

    requestStagedImage({
      // The stage suffix requestStagedImage appends is what lets a
      // standardized render replace a prepared one on screen without relying
      // on the signed URL differing between them
      // (`shared/PdfArtifactImages.md` Q3, unanswered).
      baseId: 'book-preview-' + this.book.ID + '-BODY-' + pageNumber + '-' + this.dpi,
      stage: bestAvailableStage(this.artifactStatus()),
      fetchUrl: () => this.bookService.getImageURL(this.book.ID, 'BODY', this.dpi, pageNumber),
      onReady: cachedImage => this.applyImage(cachedImage, imageRef, isEvenPage),
      onUnavailable: () => {
        // Normal while the PDF is still being processed. Nothing was cached,
        // so the next stage change produces a genuine retry.
        this.resourceService.ensureProcessedResource(this.book.body.resource_ID);
      }
    });
  }

  private applyImage(cachedImage: any, imageRef: any, isEvenPage: boolean) {
    if (!imageRef || !imageRef.nativeElement) {
      return;
    }
    imageRef.nativeElement.src = cachedImage.src;
    imageRef.nativeElement.style.display = 'block';

    // Position image at the trim offset (9px for bleed/danger zone)
    const trimLineOffset = 9;
    if (isEvenPage) {
      imageRef.nativeElement.style.left = '';
      imageRef.nativeElement.style.right = trimLineOffset + 'px';
    } else {
      imageRef.nativeElement.style.right = '';
      imageRef.nativeElement.style.left = trimLineOffset + 'px';
    }
    imageRef.nativeElement.style.top = trimLineOffset + 'px';
  }
}

